import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMyEntitlement } from "../api";
import { usePlanGateStore } from "../stores/planGateStore";

/**
 * Modal de ativação de plano particular (hub#489). Aberto pelo interceptor do axios no
 * 402 `subscription_required` (em vez de redirecionar — mata o loop do hub#389). Busca o
 * motivo (org_seat_revoked/never) para o copy e leva o corretor à seleção de plano.
 */
export function PlanActivationModal() {
  const { t } = useTranslation("subscription");
  const navigate = useNavigate();
  const open = usePlanGateStore((s) => s.open);
  const close = usePlanGateStore((s) => s.close);

  // Só busca o motivo quando o modal está aberto (entitlement é isento do gate).
  const { data } = useQuery({
    queryKey: ["entitlement", "gate"],
    queryFn: getMyEntitlement,
    enabled: open,
    staleTime: 30_000,
  });

  const reason = data?.reason === "org_seat_revoked" ? "orgSeatRevoked" : "never";

  function goToPlans() {
    close();
    navigate("/onboarding/plan");
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => (o ? undefined : close())}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-card data-[state=open]:animate-fade-up">
          <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border-soft px-5 pb-3 pt-4">
            <div className="flex min-w-0 items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                <Sparkles size={16} />
              </span>
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-[15px] font-bold tracking-tight">
                  {t(`planGate.${reason}.title`)}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="mt-0.5 text-[12px] text-ink-muted">
                  {t(`planGate.${reason}.body`)}
                </DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close
              aria-label={t("planGate.dismiss")}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-bg"
            >
              <X size={16} />
            </DialogPrimitive.Close>
          </header>

          <div className="flex flex-col gap-3 px-5 py-4">
            <Button size="lg" onClick={goToPlans}>
              {t("planGate.cta")}
            </Button>
            <button
              type="button"
              onClick={close}
              className="text-[13px] font-medium text-ink-muted hover:text-ink"
            >
              {t("planGate.later")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
